import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { getHamsKey } from '../../../services/product-to-hams-key';
import { ModalEntryPoint } from '../shared/modal-entry-point';

import { DIALOG_CONFIG } from './dialog-config';
import { entryPoint } from './entrypoint';
import { type PlanSelectionPageModalType } from './plan-selection-page-modal';

const TWC_MODAL_CONFIG_DEFAULT = {
	width: '1116px',
	height: '790px',
};

const REVERSE_TRIAL_MODAL_CONFIG = {
	width: '990px',
	height: '771px',
};

const getModalConfig = (type: PlanSelectionPageModalType) => {
	if (type === 'reverse-trial') {
		return REVERSE_TRIAL_MODAL_CONFIG;
	}
	return TWC_MODAL_CONFIG_DEFAULT;
};

export function PlanSelectionPageModal({
	type,
}: {
	type: PlanSelectionPageModalType;
}): React.JSX.Element {
	const { cloudId, product } = useEditionAwareness();
	const { formatMessage } = useIntl();

	const { width, height } = useMemo(() => getModalConfig(type), [type]);

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			hamsProductKey: getHamsKey(product),
		}),
		[cloudId, product],
	);

	const entryPointProps = useMemo(() => ({ type }), [type]);

	const modalProps = useMemo(
		() => ({
			width,
			height,
			shouldScrollInViewport: false,
			shouldCloseOnEscape: true,
			label: formatMessage({
				id: '@atlassian/edition-awareness.dialog.plan-selection-page.title',
				defaultMessage: 'Plan selection',
				description:
					'Accessible name for the plan selection dialog, announced to assistive technology users.',
			}),
		}),
		[width, height, formatMessage],
	);

	return (
		<ModalEntryPoint
			entryPointProps={entryPointProps}
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			modalProps={modalProps}
			dialogConfig={DIALOG_CONFIG}
		/>
	);
}
